import React, { useEffect } from 'react';
import { NODE_DESCRIPTIONS } from './data';
import './SkillDetailModal.css';

// Rating level descriptions (0-3 scale)
const RATING_LEVELS = {
  0: {
    title: 'Untrained',
    description: 'Never practiced or no rating',
    icon: '⚪'
  },
  1: {
    title: 'Beginner',
    description: 'Basic understanding and initial practice',
    icon: '🟡'
  },
  2: {
    title: 'Intermediate',
    description: 'Consistent execution under normal conditions',
    icon: '🟠'
  },
  3: {
    title: 'Advanced',
    description: 'Expert mastery and reliable high-level execution',
    icon: '⭐'
  }
};

export default function SkillDetailModal({
  isOpen,
  skill,
  currentRating,
  onClose,
  readOnly = true,
  branchColor = '#4ade80',
  breadcrumbPath = []
}) {
  // Handle escape key
  useEffect(() => {
    const handleEscapeKey = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    if (isOpen) {
      document.addEventListener('keydown', handleEscapeKey);
      return () => document.removeEventListener('keydown', handleEscapeKey);
    }
  }, [isOpen, onClose]);

  // Safety check: ensure component has valid data before rendering
  if (!isOpen || !skill || typeof skill !== 'object') return null;
  if (!skill.id || !onClose || typeof onClose !== 'function') return null;

  // Safely extract rating info with defaults
  const ratingIndex = typeof currentRating === 'number' ? Math.max(0, Math.min(currentRating, 3)) : 0;
  const ratingInfo = RATING_LEVELS && RATING_LEVELS[ratingIndex]
    ? RATING_LEVELS[ratingIndex]
    : {
        title: 'Untrained',
        description: 'No rating information',
        icon: '⚪'
      };

  const description = NODE_DESCRIPTIONS && NODE_DESCRIPTIONS[skill.id]
    ? NODE_DESCRIPTIONS[skill.id]
    : 'No description available';

  const maxRating = typeof skill.maxRating === 'number' ? skill.maxRating : 3;
  const skillTitle = skill.title || 'Unknown Skill';

  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  return (
    <div
      className="skill-detail-modal__overlay"
      onClick={handleBackdropClick}
    >
      <div className="skill-detail-modal__card">
        {/* Close Button */}
        <button
          className="skill-detail-modal__close"
          onClick={onClose}
          aria-label="Close skill detail modal"
          title="Close (ESC)"
        >
          ✕
        </button>

        {/* Skill Title Section */}
        <div className="skill-detail-modal__title-section">
          <h2
            className="skill-detail-modal__title"
            style={{ color: branchColor || '#4ade80' }}
          >
            {skillTitle}
          </h2>
        </div>

        {/* Divider */}
        <div className="skill-detail-modal__divider" />

        {/* Rating Description Section */}
        <div className="skill-detail-modal__rating-section">
          <div className="skill-detail-modal__rating-level">
            <span className="skill-detail-modal__rating-icon">
              {ratingInfo.icon}
            </span>
            <div className="skill-detail-modal__rating-text">
              <div
                className="skill-detail-modal__rating-title"
                style={{ color: branchColor }}
              >
                {ratingInfo.title}
              </div>
              <div className="skill-detail-modal__rating-description">
                {ratingInfo.description}
              </div>
            </div>
          </div>

          {/* Rating Dots - View Only */}
          <div className="skill-detail-modal__dots-container">
            <div className="skill-detail-modal__dots">
              {Array.from({ length: Math.max(1, maxRating) }, (_, i) => i + 1).map(level => (
                <div
                  key={level}
                  className={`skill-detail-modal__dot ${
                    level <= ratingIndex ? 'skill-detail-modal__dot--filled' : 'skill-detail-modal__dot--empty'
                  }`}
                  style={{
                    backgroundColor: level <= ratingIndex ? (branchColor || '#4ade80') : 'rgba(212, 165, 116, 0.2)',
                  }}
                  title={`Level ${level}`}
                />
              ))}
            </div>
            <div className="skill-detail-modal__dots-label">
              {ratingIndex}/{maxRating}
            </div>
          </div>
        </div>

        {/* Divider */}
        <div className="skill-detail-modal__divider" />

        {/* Skill Description Section */}
        <div className="skill-detail-modal__description-section">
          <div className="skill-detail-modal__description-label">
            Skill Details
          </div>
          <p className="skill-detail-modal__description-text">
            {description}
          </p>
        </div>

        {/* Breadcrumb - Optional */}
        {breadcrumbPath && breadcrumbPath.length > 0 && (
          <div className="skill-detail-modal__breadcrumb">
            <span className="skill-detail-modal__breadcrumb-label">Path:</span>
            <span className="skill-detail-modal__breadcrumb-path">
              {breadcrumbPath.map(item => item.title).join(' > ')}
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
